import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X, Plus, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ProductCard from './ProductCard';
import { CatalogAPI, SiteConfigAPI, resolveImage } from '../lib/api';
import { useSiteConfig } from '../context/SiteConfigContext';
import { useAdminEdit } from '../context/AdminEditContext';
import { useToast } from '../hooks/use-toast';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

const SortablePopular = ({ p, onAddToCart, onRemove, idx }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 'auto' };
  return (
    <div ref={setNodeRef} style={style} className="relative">
      <button onClick={() => onRemove(p.id)} title="Retirer des populaires"
        className="absolute -top-2 -right-2 z-20 bg-[#C75B27] text-white rounded-full w-7 h-7 flex items-center justify-center shadow hover:bg-[#a84d21]">
        <X className="w-4 h-4" />
      </button>
      <button {...attributes} {...listeners} title="Glisser pour déplacer"
        className="absolute -top-2 -left-2 z-20 bg-[#1F4D3F] text-white rounded-full w-7 h-7 flex items-center justify-center shadow cursor-grab active:cursor-grabbing touch-none">
        <GripVertical className="w-4 h-4" />
      </button>
      <ProductCard product={p} onAddToCart={onAddToCart} />
    </div>
  );
};

const PopularProducts = ({ onAddToCart }) => {
  const { t } = useTranslation();
  const { config, refresh } = useSiteConfig();
  const { editMode } = useAdminEdit();
  const { toast } = useToast();
  const [autoProducts, setAutoProducts] = useState([]);
  const [addedProducts, setAddedProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const excluded = Array.isArray(config['home.popular_excluded']) ? config['home.popular_excluded'] : [];
  const addedIds = Array.isArray(config['home.popular_added']) ? config['home.popular_added'] : [];

  useEffect(() => {
    CatalogAPI.products({ sort: 'popular', limit: 16 }).then(setAutoProducts).catch(() => {});
  }, []);

  useEffect(() => {
    if (addedIds.length === 0) { setAddedProducts([]); return; }
    Promise.all(addedIds.map(id => CatalogAPI.product(id).catch(() => null))).then(r => setAddedProducts(r.filter(Boolean)));
  }, [JSON.stringify(addedIds)]);

  useEffect(() => {
    if (search.length < 2) { setSearchResults([]); return; }
    const tmr = setTimeout(() => {
      CatalogAPI.products({ q: search, limit: 8 }).then(setSearchResults).catch(() => {});
    }, 300);
    return () => clearTimeout(tmr);
  }, [search]);

  const saveConfig = async (key, value) => {
    try {
      const full = await SiteConfigAPI.adminGet();
      full[key] = value;
      await SiteConfigAPI.adminSet(full);
      await refresh();
    } catch (e) { toast({ title: 'Erreur' }); }
  };

  const removeProduct = (id) => {
    if (addedIds.includes(id)) {
      saveConfig('home.popular_added', addedIds.filter(x => x !== id));
    } else {
      saveConfig('home.popular_excluded', [...excluded, id]);
    }
    toast({ title: 'Produit retiré des populaires' });
  };

  const addProduct = (id) => {
    if (!addedIds.includes(id)) saveConfig('home.popular_added', [...addedIds, id]);
    setSearch(''); setSearchResults([]);
    toast({ title: 'Produit ajouté aux populaires' });
  };

  // Composition finale : auto (sans exclus) + ajoutés, dédupliqués
  const autoFiltered = autoProducts.filter(p => !excluded.includes(p.id) && !addedIds.includes(p.id));
  const display = [...addedProducts, ...autoFiltered].slice(0, editMode ? 99 : 8);

  return (
    <section className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F4D3F]">{t('home.popular_title')}</h2>
          <p className="text-[#7A6F5C] mt-2">{t('home.popular_subtitle')}</p>
        </div>
        <Link to="/categories" className="hidden md:inline-flex items-center gap-1 text-[#1F4D3F] hover:text-[#173B30] font-medium">
          {t('common.view_more')} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {editMode && (
        <div className="bg-[#FAF7F2] border border-[#E8E0D0] rounded-xl p-3 mb-4">
          <div className="text-xs text-[#7A6F5C] mb-2">Section automatique (produits les plus vus). Vous pouvez retirer des produits (croix) ou en ajouter ci-dessous.</div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A8F82]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ajouter un produit aux populaires…"
              className="w-full pl-9 pr-3 py-2 border border-[#E8E0D0] rounded-lg text-sm focus:outline-none" />
          </div>
          {searchResults.length > 0 && (
            <div className="mt-2 border border-[#E8E0D0] rounded-lg divide-y divide-[#F5F1EA] max-h-56 overflow-y-auto bg-white">
              {searchResults.map(r => (
                <div key={r.id} className="flex items-center gap-3 p-2">
                  <img src={resolveImage(r.image)} alt="" className="w-9 h-9 rounded object-cover bg-[#FAF7F2]" />
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{r.name}</div></div>
                  {addedIds.includes(r.id) ? <span className="text-xs text-emerald-700">Déjà ajouté</span>
                    : <button onClick={() => addProduct(r.id)} className="bg-[#1F4D3F] text-white text-xs px-3 py-1.5 rounded flex items-center gap-1"><Plus className="w-3 h-3" />Ajouter</button>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editMode ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            const displayIds = display.map(x => x.id);
            const oldI = displayIds.indexOf(active.id), newI = displayIds.indexOf(over.id);
            if (oldI < 0 || newI < 0) return;
            const newOrder = arrayMove(displayIds, oldI, newI);
            saveConfig('home.popular_added', newOrder);
            toast({ title: 'Ordre mis à jour' });
          }}>
          <SortableContext items={display.map(x => x.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {display.map((p) => (
                <SortablePopular key={p.id} p={p} onAddToCart={onAddToCart} onRemove={removeProduct} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {display.map((p, idx) => (
            <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} sponsored={idx === 2 || idx === 5} />
          ))}
        </div>
      )}
    </section>
  );
};

export default PopularProducts;
